import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';
import { useToast } from '@/hooks/ui/use-toast';
import { supabaseFunctionWithRetry, supabaseQueryWithRetry } from '@/lib/networkRetry';
import { rateLimiters } from '@/lib/throttle';
import { debouncedRefetch } from '@/lib/debouncedRefetch';
import { QUERY_CONFIG } from '@/config/constants';
import { QUERY_KEYS } from '@/config/queries';
import { WEARABLE_CONFIG } from '@/config/wearable';
import { createLogger } from '@/lib/structuredLogger';
import { getLocalIsoDate, getUserTimezone } from '@/lib/dateUtils';
import { useWearableConnectionStatus } from '@/hooks/wearable/use-wearable-connection-status';
import { invalidateWearableQueries } from '@/lib/wearableQueryInvalidation';
import {
  hasRecoveryMetrics,
  mergeMissingRecoveryMetrics,
  type RecoveryMetricFallback,
  type HealthMetrics,
} from '@/lib/data/dashboard';
import { disconnectWearableIntegration } from '@/lib/data/profile';

interface UseFitbitSyncOptions {
  userId?: string;
  paused?: boolean;
}

function hasScheduledScoreRecalculation(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') return false;

  const scoreRecalculation = (payload as { score_recalculation?: unknown }).score_recalculation;
  if (!scoreRecalculation || typeof scoreRecalculation !== 'object') return false;

  return (scoreRecalculation as { status?: unknown }).status === 'scheduled';
}

function isTerminalFitbitBackgroundSyncError(message: string): boolean {
  return /rate limit|quota|consent|health data processing|reconnect|re-login|authentication expired|unauthorized|forbidden|sync already in progress|already in progress/i.test(
    message,
  );
}

export function pickLatestLastSyncTime(
  candidates: Array<string | null | undefined>,
): string | null {
  let latest: { value: string; timestamp: number } | null = null;

  for (const candidate of candidates) {
    if (!candidate) continue;

    const timestamp = Date.parse(candidate);
    if (!Number.isFinite(timestamp)) continue;

    if (!latest || timestamp > latest.timestamp) {
      latest = { value: candidate, timestamp };
    }
  }

  return latest?.value ?? null;
}

export function useFitbitSync({ userId, paused = false }: UseFitbitSyncOptions) {
  const { t } = useTranslation();
  const logger = createLogger('useFitbitSync');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isConnecting, setIsConnecting] = useState(false);
  const [syncFailCount, setSyncFailCount] = useState(0);
  const [syncAttemptCount, setSyncAttemptCount] = useState(0);
  const lastResumeSyncRef = useRef<Date | null>(null);
  const lastSyncErrorToastRef = useRef<string | null>(null);
  const backgroundSyncInFlightRef = useRef(false);

  // Check if Fitbit is connected
  const { data: fitbitStatus, isLoading: statusLoading } = useWearableConnectionStatus({
    userId,
  });

  // Fetch rate limit status to prevent frontend/backend sync conflicts
  const { data: syncStatus } = useQuery({
    queryKey: QUERY_KEYS.FITBIT_SYNC_STATUS(userId ?? ''),
    queryFn: async (): Promise<Tables<'fitbit_sync_status'> | null> => {
      if (!userId) return null;
      const { data } = await supabaseQueryWithRetry<Tables<'fitbit_sync_status'>>(() =>
        supabase
          .from('fitbit_sync_status')
          .select(
            'requests_this_hour, quota_reset_at, next_allowed_sync, sync_state, sync_progress, sync_scope, last_error, updated_at',
          )
          .eq('user_id', userId)
          .maybeSingle(),
      );

      return data;
    },
    enabled: !!userId && fitbitStatus?.provider === 'fitbit',
    staleTime: 0,
    gcTime: QUERY_CONFIG.GC_TIME.SHORT,
    refetchInterval: paused
      ? false
      : (query) => {
          const state = query.state.data?.sync_state;
          return state === 'syncing' ? 3000 : 30000;
        },
  });

  // Surface sync errors (e.g. missing consent) once per session.
  // Use sessionStorage to prevent re-showing across re-mounts / navigations.
  useEffect(() => {
    if (!userId || !fitbitStatus?.connected) return;
    const lastError = syncStatus?.last_error as string | null | undefined;
    const sessionKey = 'fitbit_error_shown';

    if (!lastError || syncStatus?.sync_state !== 'failed') {
      lastSyncErrorToastRef.current = null;
      sessionStorage.removeItem(sessionKey);
      return;
    }

    // Skip stale errors older than 1 hour
    const updatedAt = syncStatus?.updated_at as string | null | undefined;
    if (updatedAt) {
      const ageMs = Date.now() - new Date(updatedAt).getTime();
      if (ageMs > 60 * 60 * 1000) return;
    }

    // Deduplicate within this component instance
    if (lastSyncErrorToastRef.current === lastError) return;

    // Deduplicate across re-mounts using sessionStorage
    const alreadyShown = sessionStorage.getItem(sessionKey);
    if (alreadyShown === lastError) return;

    lastSyncErrorToastRef.current = lastError;
    sessionStorage.setItem(sessionKey, lastError);

    if (window.location.pathname.startsWith('/profile')) return;

    toast({
      title: t('fitbit.syncFailed'),
      description: lastError,
      variant: 'warning',
      actionLabel: t('profile.title'),
      onAction: () => window.location.assign('/profile'),
    });
  }, [
    fitbitStatus?.connected,
    syncStatus?.last_error,
    syncStatus?.sync_state,
    syncStatus?.updated_at,
    t,
    toast,
    userId,
  ]);

  // Fetch latest Fitbit data
  const { data: fitbitData, isLoading: dataLoading } = useQuery({
    queryKey: QUERY_KEYS.FITBIT_DATA(userId ?? 'anonymous'),
    queryFn: async () => {
      if (!userId) return {};

      const today = getLocalIsoDate();
      logger.debug('Fetching health_metrics from database', { date: today });

      const { data: exactMetrics, error: exactError } = await supabaseQueryWithRetry<
        Partial<HealthMetrics>
      >(async () => {
        const result = await supabase
          .from('health_metrics')
          .select('*')
          .eq('user_id', userId)
          .eq('metric_date', today)
          .maybeSingle();
        return {
          data: (result.data as unknown as Partial<HealthMetrics> | null) ?? null,
          error: result.error,
        };
      });

      if (exactError) {
        logger.error(
          'Error fetching health_metrics exact day',
          exactError instanceof Error ? exactError : new Error(String(exactError)),
          { date: today },
        );
      }

      let healthMetrics = exactMetrics;
      if (healthMetrics && !hasRecoveryMetrics(healthMetrics)) {
        const { data: recentRecoveryRows, error: recoveryError } = await supabaseQueryWithRetry<
          RecoveryMetricFallback[]
        >(async () => {
          const result = await supabase
            .from('health_metrics')
            .select(
              'metric_date, sleep_hours, sleep_efficiency, sleep_deep_minutes, sleep_rem_minutes, sleep_start_time, hrv_rmssd, hrv_coverage',
            )
            .eq('user_id', userId)
            .lte('metric_date', today)
            .order('metric_date', { ascending: false })
            .limit(7);
          return { data: result.data, error: result.error };
        });

        if (recoveryError) {
          logger.warn('Error fetching Fitbit recovery fallback health_metrics', {
            date: today,
            error: recoveryError instanceof Error ? recoveryError.message : String(recoveryError),
          });
        } else {
          const recoveryMetrics = recentRecoveryRows?.find((row) =>
            hasRecoveryMetrics(row as unknown as Partial<HealthMetrics>),
          );
          healthMetrics = mergeMissingRecoveryMetrics(
            healthMetrics,
            (recoveryMetrics as unknown as Partial<HealthMetrics> | null) ?? null,
          );
        }
      }

      if (!healthMetrics) {
        // Fallback for timezone boundary mismatches: use latest available metric row
        const { data: recentMetrics, error: recentError } = await supabaseQueryWithRetry<
          Partial<HealthMetrics>
        >(async () => {
          const result = await supabase
            .from('health_metrics')
            .select('*')
            .eq('user_id', userId)
            .lte('metric_date', today)
            .order('metric_date', { ascending: false })
            .limit(1)
            .maybeSingle();
          return {
            data: (result.data as unknown as Partial<HealthMetrics> | null) ?? null,
            error: result.error,
          };
        });

        if (recentError) {
          logger.warn('Error fetching fallback recent health_metrics', {
            date: today,
            error: recentError instanceof Error ? recentError.message : String(recentError),
          });
        }

        healthMetrics = recentMetrics;
      }

      if (!healthMetrics) {
        // Final fallback: aggregated latest sync values
        const { data: healthSummary, error: summaryError } = await supabaseQueryWithRetry<
          Tables<'user_health_data'>
        >(() =>
          supabase
            .from('user_health_data')
            .select(
              'steps, calories, distance, active_minutes, sleep_hours, heart_rate, floors, weight, body_fat, oxygen_sat, resp_rate, temperature, cardio_score, hrv_rmssd, hrv_coverage',
            )
            .eq('user_id', userId)
            .maybeSingle(),
        );

        if (summaryError) {
          logger.warn('Error fetching fallback user_health_data', {
            error: summaryError instanceof Error ? summaryError.message : String(summaryError),
          });
          return {};
        }

        if (!healthSummary) return {};

        return {
          steps: healthSummary.steps || 0,
          calories: healthSummary.calories || 0,
          distance: healthSummary.distance || 0,
          active_minutes: healthSummary.active_minutes || 0,
          sleep_hours: healthSummary.sleep_hours || 0,
          heart_rate: healthSummary.heart_rate || 0,
          floors: healthSummary.floors || 0,
          weight: healthSummary.weight || 0,
          body_fat: healthSummary.body_fat || 0,
          oxygen_saturation: healthSummary.oxygen_sat || 0,
          respiratory_rate: healthSummary.resp_rate || 0,
          temperature: healthSummary.temperature || 0,
          cardio_fitness: healthSummary.cardio_score || 0,
          hrv_rmssd: healthSummary.hrv_rmssd || 0,
          hrv_coverage: healthSummary.hrv_coverage || 0,
          hrv_deep_rmssd: 0,
        };
      }

      return {
        steps: healthMetrics.steps || 0,
        calories: healthMetrics.calories || 0,
        distance: healthMetrics.distance || 0,
        active_minutes: healthMetrics.active_minutes || 0,
        sleep_hours: healthMetrics.sleep_hours || 0,
        heart_rate: healthMetrics.heart_rate || 0,
        floors: healthMetrics.floors || 0,
        weight: healthMetrics.weight || 0,
        body_fat: healthMetrics.body_fat || 0,
        oxygen_saturation: healthMetrics.oxygen_saturation || 0,
        respiratory_rate: healthMetrics.respiratory_rate || 0,
        temperature: healthMetrics.temperature || 0,
        cardio_fitness: healthMetrics.cardio_fitness || 0,
        // HRV fields for SOTA recovery scoring
        hrv_rmssd: healthMetrics.hrv_rmssd || 0,
        hrv_coverage: healthMetrics.hrv_coverage || 0,
        hrv_deep_rmssd: healthMetrics.hrv_deep_rmssd || 0,
      };
    },
    enabled: fitbitStatus?.connected && !!userId,
    staleTime: QUERY_CONFIG.STALE_TIME.LONG,
    gcTime: QUERY_CONFIG.GC_TIME.MEDIUM,
  });

  // Fetch raw sleep data
  const { data: rawSleepData } = useQuery({
    queryKey: QUERY_KEYS.FITBIT_RAW_SLEEP(userId ?? ''),
    queryFn: async () => {
      if (!userId) return null;
      const today = getLocalIsoDate();
      const { data } = await supabaseQueryWithRetry<Tables<'fitbit_data'>>(() =>
        supabase
          .from('fitbit_data')
          .select('value')
          .eq('user_id', userId)
          .eq('data_type', 'sleep')
          .eq('data_date', today)
          .maybeSingle(),
      );
      return data?.value || null;
    },
    enabled: !!userId && fitbitStatus?.provider === 'fitbit',
    staleTime: QUERY_CONFIG.STALE_TIME.LONG,
    gcTime: QUERY_CONFIG.GC_TIME.MEDIUM,
  });

  // Fetch the connected provider's last sync time for the header indicator.
  const { data: lastSyncTime } = useQuery({
    queryKey: QUERY_KEYS.FITBIT_LAST_SYNC_PROVIDER(
      userId ?? 'anonymous',
      fitbitStatus?.provider ?? 'none',
      fitbitStatus?.wearableProvider ?? 'none',
    ),
    queryFn: async () => {
      if (!userId) return null;

      if (fitbitStatus?.provider === 'wearable') {
        let query = supabase
          .from('wearable_connections')
          .select('last_synced_at, last_successful_sync_at')
          .eq('user_id', userId)
          .eq('status', 'active');

        if (fitbitStatus.wearableProvider) {
          query = query.eq('provider', fitbitStatus.wearableProvider);
        }

        const { data, error } = await supabaseQueryWithRetry<
          Pick<Tables<'wearable_connections'>, 'last_synced_at' | 'last_successful_sync_at'>[]
        >(async () => {
          const result = await query;
          return { data: result.data, error: result.error };
        });
        if (error) throw error;

        return pickLatestLastSyncTime(
          (data ?? []).flatMap((connection) => [
            connection.last_successful_sync_at,
            connection.last_synced_at,
          ]),
        );
      }

      const [{ data: fitbitDataRow }, { data: syncHistoryRow }, { data: syncStatusRow }] =
        await Promise.all([
          supabaseQueryWithRetry<Tables<'fitbit_data'>>(() =>
            supabase
              .from('fitbit_data')
              .select('synced_at')
              .eq('user_id', userId)
              .order('synced_at', { ascending: false })
              .limit(1)
              .maybeSingle(),
          ),
          supabaseQueryWithRetry<Tables<'fitbit_sync_status_history'>>(() =>
            supabase
              .from('fitbit_sync_status_history')
              .select('sync_ended_at, sync_started_at, created_at')
              .eq('user_id', userId)
              .eq('sync_state', 'completed')
              .order('sync_started_at', { ascending: false })
              .limit(1)
              .maybeSingle(),
          ),
          supabaseQueryWithRetry<Tables<'fitbit_sync_status'>>(() =>
            supabase
              .from('fitbit_sync_status')
              .select('last_successful_sync')
              .eq('user_id', userId)
              .maybeSingle(),
          ),
        ]);

      return pickLatestLastSyncTime([
        fitbitDataRow?.synced_at,
        syncHistoryRow?.sync_ended_at,
        syncHistoryRow?.sync_started_at,
        syncHistoryRow?.created_at,
        syncStatusRow?.last_successful_sync,
      ]);
    },
    enabled: !!userId && !!fitbitStatus?.connected && !!fitbitStatus?.provider,
    staleTime: 30_000, // 30 seconds
    gcTime: QUERY_CONFIG.GC_TIME.SHORT,
  });

  const runDirectHistoricalSync = useCallback(
    async (options?: { days?: number; force_refresh?: boolean }) => {
      if (!userId) throw new Error('User ID required for sync');
      const today = getLocalIsoDate();
      const syncLastNDays = options?.days ?? WEARABLE_CONFIG.HISTORICAL_SYNC_DAYS;

      const { data, error } = await supabaseFunctionWithRetry(supabase, 'fitbit-sync', {
        body: {
          date: today,
          force_refresh: options?.force_refresh ?? true,
          timezone: getUserTimezone(),
          sync_last_n_days: syncLastNDays,
        },
      });

      if (error) throw error;

      if (
        data &&
        typeof data === 'object' &&
        'success' in data &&
        (data as { success?: boolean }).success === false
      ) {
        const payload = data as { error?: string; message?: string };
        throw new Error(payload.error || payload.message || 'Sync failed');
      }

      return data;
    },
    [userId],
  );

  // Resume backfill functionality - triggers historical sync when app becomes visible after being offline
  // NOTE: This useEffect must come AFTER fitbitStatus query is defined
  useEffect(() => {
    if (!userId || fitbitStatus?.provider !== 'fitbit') return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      if (backgroundSyncInFlightRef.current) return;

      const now = new Date();
      const lastSync = lastResumeSyncRef.current;

      if (
        !lastSync ||
        now.getTime() - lastSync.getTime() > WEARABLE_CONFIG.RESUME_SYNC_COOLDOWN_MS
      ) {
        backgroundSyncInFlightRef.current = true;
        logger.info('App resumed, triggering background sync');

        try {
          await supabaseFunctionWithRetry(supabase, 'fitbit-background-sync', {
            body: {
              user_id: userId,
              force_refresh: false,
            },
          });

          lastResumeSyncRef.current = now;
          await invalidateWearableQueries(queryClient, userId);
        } catch (error) {
          logger.error(
            'Resume backfill sync failed',
            error instanceof Error ? error : new Error(String(error)),
            { userId },
          );
        } finally {
          backgroundSyncInFlightRef.current = false;
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userId, fitbitStatus?.provider, queryClient, logger]);

  // Connect mutation
  const connectMutation = useMutation({
    mutationFn: async () => {
      setIsConnecting(true);
      logger.info('Starting OAuth flow');

      const { data, error } = await supabaseFunctionWithRetry<{ authUrl: string }>(
        supabase,
        'fitbit-oauth-initiate',
      );

      if (error) throw error;
      if (!data?.authUrl) throw new Error('No authorization URL returned');

      // Store redirect destination for after OAuth - redirect to dashboard
      try {
        localStorage.setItem('post_fitbit_connect_redirect', '/dashboard');
      } catch (e) {
        logger.warn(
          'Failed to save redirect destination',
          e instanceof Error ? e : new Error(String(e)),
        );
      }

      // Always use same-tab redirect (no popup) for consistent UX
      window.location.href = data.authUrl;
      return Promise.resolve(true);
    },
    onSuccess: () => {
      toast({
        title: t('fitbit.connected'),
        description: t('fitbit.syncingData'),
      });

      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FITBIT_STATUS() });
      if (userId) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FITBIT_STATUS_USER(userId) });
      }
      if (userId) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FITBIT_DATA(userId) });
      }
      queryClient.refetchQueries({ queryKey: QUERY_KEYS.FITBIT_STATUS() });
      if (userId) {
        queryClient.refetchQueries({ queryKey: QUERY_KEYS.FITBIT_STATUS_USER(userId) });
      }
    },
    onError: (error: Error) => {
      if (error.message !== 'Connection cancelled by user') {
        toast({
          title: t('fitbit.connectionFailed'),
          description: error.message,
          variant: 'destructive',
        });
      }
      setIsConnecting(false);
    },
  });

  // Sync mutation
  const syncMutation = useMutation({
    mutationFn: async (options?: {
      silent?: boolean;
      force_refresh?: boolean;
      sync_last_n_days?: number;
    }): Promise<unknown> => {
      const isWearable = fitbitStatus?.provider === 'wearable';
      if (isWearable && !fitbitStatus?.wearableProvider) {
        throw new Error('No wearable provider connected');
      }

      if (!isWearable && !rateLimiters.fitbitSync.canProceed()) {
        throw new Error('Rate limit exceeded. Please wait before syncing again.');
      }

      const syncLastNDays = options?.sync_last_n_days ?? WEARABLE_CONFIG.DEFAULT_SYNC_DAYS;

      const { data, error } = isWearable
        ? await supabaseFunctionWithRetry(supabase, 'wearable-sync', {
            body: {
              provider: fitbitStatus?.wearableProvider ?? undefined,
              sync_last_n_days: syncLastNDays,
              timezone: getUserTimezone(),
            },
          })
        : await supabaseFunctionWithRetry(supabase, 'fitbit-sync', {
            body: {
              date: getLocalIsoDate(),
              force_refresh: options?.force_refresh ?? !options?.silent,
              timezone: getUserTimezone(),
              sync_last_n_days: syncLastNDays,
            },
          });

      if (error) throw error;

      if (
        data &&
        typeof data === 'object' &&
        'success' in data &&
        (data as { success?: boolean }).success === false
      ) {
        const payload = data as { error?: string; message?: string };
        throw new Error(payload.error || payload.message || 'Sync failed');
      }

      return data;
    },
    onMutate: () => {
      // Force immediate check of sync status to catch the "0%" state
      logger.info('Sync mutation started, invalidating status');
      if (userId && fitbitStatus?.connected) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FITBIT_SYNC_STATUS(userId ?? '') });
      }
    },
    onSuccess: async (data, variables) => {
      setSyncFailCount(0);

      const isWearable = fitbitStatus?.provider === 'wearable';

      if (!variables?.silent) {
        if (!isWearable && (data as { rateLimited?: boolean } | null)?.rateLimited) {
          toast({
            title: t('fitbit.rateLimited'),
            description: t('fitbit.rateLimitedDescription'),
            variant: 'default',
          });
        } else {
          toast({
            title: isWearable ? t('wearables.syncSuccess') : t('fitbit.syncSuccess'),
            description: isWearable ? t('wearables.syncSuccess') : t('fitbit.syncSuccess'),
          });
        }
      }

      const serverScheduledScoreRecalculation = hasScheduledScoreRecalculation(data);

      if (serverScheduledScoreRecalculation) {
        logger.info('Server scheduled score recalculation after sync');
      } else {
        try {
          await supabaseFunctionWithRetry(supabase, 'calculate-health-points', {
            body: { date: getLocalIsoDate(), timezone: getUserTimezone() },
          });
        } catch (e) {
          logger.warn(
            'calculate-health-points invocation failed',
            e instanceof Error ? e : new Error(String(e)),
          );
        }
      }

      // Immediately invalidate sync status to reflect completion
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FITBIT_SYNC_STATUS(userId ?? '') });

      // Refresh dashboard and health data after short delay
      setTimeout(() => {
        if (userId) {
          invalidateWearableQueries(queryClient, userId).catch((error) => {
            logger.warn('Failed to invalidate wearable queries after sync', {
              userId,
              error: error instanceof Error ? error.message : String(error),
            });
          });
          debouncedRefetch(queryClient, QUERY_KEYS.FITBIT_DATA(userId), 500);
          debouncedRefetch(queryClient, QUERY_KEYS.DASHBOARD(userId), 500);
          debouncedRefetch(queryClient, QUERY_KEYS.DASHBOARD_METRICS(userId), 500);
          debouncedRefetch(queryClient, QUERY_KEYS.POINTS(userId), 500);
          debouncedRefetch(queryClient, QUERY_KEYS.STREAK(userId), 500);
          debouncedRefetch(queryClient, QUERY_KEYS.HEALTH_SCORE(userId), 500);
        }
      }, 500);

      if (serverScheduledScoreRecalculation) {
        setTimeout(() => {
          if (!userId) return;
          debouncedRefetch(queryClient, QUERY_KEYS.POINTS(userId), 500);
          debouncedRefetch(queryClient, QUERY_KEYS.STREAK(userId), 500);
          debouncedRefetch(queryClient, QUERY_KEYS.HEALTH_SCORE(userId), 500);
          debouncedRefetch(queryClient, QUERY_KEYS.DASHBOARD(userId), 500);
        }, 3000);
      }
    },
    onError: (error: Error, variables) => {
      setSyncFailCount((prev) => prev + 1);

      if (variables?.silent) return;

      if (syncFailCount >= WEARABLE_CONFIG.MAX_SYNC_FAILURES - 1) {
        toast({
          title:
            fitbitStatus?.provider === 'wearable'
              ? t('wearables.syncFailed')
              : t('fitbit.syncFailed'),
          description:
            fitbitStatus?.provider === 'wearable'
              ? t('wearables.syncFailedDescription')
              : 'Too many sync failures. Please try reconnecting your Fitbit account.',
          variant: 'destructive',
        });
        return;
      }

      toast({
        title:
          fitbitStatus?.provider === 'wearable'
            ? t('wearables.syncFailed')
            : t('fitbit.syncFailed'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Disconnect mutation
  const disconnectMutation = useMutation({
    mutationFn: async () => {
      const isWearable = fitbitStatus?.provider === 'wearable';
      const confirmed = window.confirm(
        isWearable ? t('wearables.disconnectConfirm') : t('fitbit.disconnectConfirm'),
      );
      if (!confirmed) throw new Error('Disconnect cancelled by user');

      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();
      if (authError || !user) throw new Error('Not authenticated');

      await disconnectWearableIntegration({
        userId: user.id,
        provider: isWearable ? (fitbitStatus?.wearableProvider ?? fitbitStatus.provider) : 'fitbit',
      });

      localStorage.removeItem('fitbit_ai_data');
    },
    onSuccess: () => {
      toast({
        title:
          fitbitStatus?.provider === 'wearable'
            ? t('wearables.disconnected')
            : t('fitbit.disconnected'),
        description:
          fitbitStatus?.provider === 'wearable'
            ? t('wearables.disconnectSuccess')
            : t('fitbit.disconnectSuccess'),
      });
      if (userId) {
        void invalidateWearableQueries(queryClient, userId, { includeConnectionStatus: true });
      }

      setTimeout(() => window.location.reload(), 500);
    },
    onError: (error: Error) => {
      toast({
        title:
          fitbitStatus?.provider === 'wearable'
            ? t('wearables.disconnectFailed')
            : t('fitbit.disconnectFailed'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const refreshConnectionStatus = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FITBIT_STATUS() });
    if (userId) {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FITBIT_STATUS_USER(userId) });
    }
    if (userId) {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FITBIT_DATA(userId) });
    }
    queryClient.refetchQueries({ queryKey: QUERY_KEYS.FITBIT_STATUS() });
    if (userId) {
      queryClient.refetchQueries({ queryKey: QUERY_KEYS.FITBIT_STATUS_USER(userId) });
    }
  }, [queryClient, userId]);

  // Background historical sync mutation (for manual triggers)
  const backgroundSyncMutation = useMutation({
    mutationFn: async (options?: { days?: number; force_refresh?: boolean }) => {
      if (!userId) throw new Error('User ID required for background sync');
      if (fitbitStatus?.provider !== 'fitbit') {
        throw new Error('Background sync is only available for Fitbit connections.');
      }

      logger.info('Triggering manual background historical sync', options);

      const { data, error } = await supabaseFunctionWithRetry(supabase, 'fitbit-background-sync', {
        body: {
          user_id: userId,
          force_refresh: options?.force_refresh ?? false,
          days: options?.days,
        },
      });

      const maybeErrors = (data as { results?: { errors?: string[] } } | null)?.results?.errors;
      const backgroundError =
        error?.message || (maybeErrors && maybeErrors.length > 0 ? maybeErrors[0] : null);

      if (backgroundError) {
        if (isTerminalFitbitBackgroundSyncError(backgroundError)) {
          throw new Error(backgroundError);
        }

        logger.warn('Background sync failed, falling back to direct historical sync', {
          error: backgroundError,
          days: options?.days,
        });
        return runDirectHistoricalSync(options);
      }

      return data;
    },
    onSuccess: () => {
      logger.info('Manual background sync completed');
      if (!userId) return;
      invalidateWearableQueries(queryClient, userId).catch((error) => {
        logger.warn('Failed to invalidate wearable queries after background sync', {
          userId,
          error: error instanceof Error ? error.message : String(error),
        });
      });
    },
    onError: (error: Error) => {
      logger.error(
        'Manual background sync failed',
        error instanceof Error ? error : new Error(String(error)),
      );
      toast({
        title: t('fitbit.syncFailed'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    // Status
    fitbitStatus,
    statusLoading,
    isConnecting,

    // Data
    fitbitData,
    dataLoading,
    rawSleepData,

    // Sync state
    syncFailCount,
    syncAttemptCount,
    setSyncAttemptCount,
    syncStatus, // Rate limit status for frontend sync coordination
    syncProgress: syncStatus?.sync_progress || 0,
    syncState: syncStatus?.sync_state,
    syncScope: syncStatus?.sync_scope,
    syncError: (syncStatus?.last_error as string | null | undefined) ?? null,
    lastSyncTime, // Last sync timestamp for UI indicator
    isSyncing:
      syncMutation.isPending ||
      backgroundSyncMutation.isPending ||
      syncStatus?.sync_state === 'syncing',

    // Mutations
    connectMutation,
    syncMutation,
    disconnectMutation,
    backgroundSyncMutation,

    // Helpers
    refreshConnectionStatus,
  };
}
